import { useState, useRef, useEffect, useCallback } from "react";

export function useSpeechRecognition({ onTranscript, onStart, onEnd, onError }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");

  const recognitionRef = useRef(null);
  const speechTimeoutRef = useRef(null);
  const restartTimerRef = useRef(null);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.warn("Could not start recognition:", e);
      }
    }
  }, [isListening]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn("Speech Recognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
      onStart?.();
    };

    recognition.onresult = (event) => {
      if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);

      let finalTranscript = "";
      let interimTranscript = "";
      for (let i = 0; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setTranscript(finalTranscript + interimTranscript);

      if (finalTranscript.trim()) {
        speechTimeoutRef.current = setTimeout(() => {
          onTranscript(finalTranscript.trim());
          setTranscript("");
          stopListening();
        }, 1500);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "network") {
        onError?.("Network error. Speech recognition unavailable.");
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        onError?.(event.error);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      onEnd?.();
    };

    recognitionRef.current = recognition;
    const restartTimer = restartTimerRef.current;

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      if (restartTimer) clearTimeout(restartTimer);
    };
  }, [onTranscript, onStart, onEnd, onError, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return { isListening, transcript, startListening, stopListening, toggleListening };
}
